import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Flex, Text, Section, IconButton, ScrollArea } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import { Cross2Icon } from "@radix-ui/react-icons";
import { useAuth0 } from "@auth0/auth0-react";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";
import { NoteRemoveIcon } from "@/v2/assets/icons/NoteRemoveIcon";
import { useOpportunityGroups } from "@/api/useOpportunityGroups";
import { Search } from "@/components/shared/inputs/search";
import { MultiSelect } from "@/components/shared/inputs/multi-select";
import { DateRangePicker } from "@/components/shared/date-range-picker/DateRangePicker";
import { PageEmptyState } from "@/components/page-empty-state/PageEmptyState";
import { SimpleTable } from "@/v2/components/table/SimpleTable";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useOnboarding } from "@/api/onboarding";
import { Link } from "@/components/shared/link/Link";
import { useKnowledgeTracking } from "@/features/knowledge/useKnowledgeTracking";
import Select from "@/components/shared/select";
import { useExploreValues } from "@/v2/entities/explore/Explore.api";

import { OpportunityStatus, opportunityStatusesToLabels } from "./index.types";
import styles from "./styles.module.scss";
import {
  OpportunityGroupRow,
  KnowledgeGapTicketsTrend,
  KnowledgeGapPriority,
} from "./knowledge-gaps-table.types";
import {
  CheckCircledIcon,
  CircleIcon,
  MinusCircledIcon,
  QuestionMarkCircledIcon,
} from "@radix-ui/react-icons";
import { toCamelCaseWithFirstLetterCaps } from "@/assets/functions";
import { useKnowledgeGapsSearchParams } from "./useKnowledgeGapsSearchParams";
import { KnowledgeGapDetail } from "./KnowledgeGapDetail";

export const KnowledgeGapsPage = () => {
  const navigate = useNavigate();
  const { hasKnowledgeBaseIntegration } = useOnboarding();
  const { trackFilterApplied, trackKnowledgeGapViewed, trackDateRangeChanged } = useKnowledgeTracking();

  const { user } = useAuth0();
  const { data: ticketValues } = useExploreValues({
    source: "tickets",
  });

  const [domain, setDomain] = useState<string | undefined>(undefined);

  const {
    searchPhrase,
    setSearchPhrase,
    status,
    setStatus,
    dateRange,
    setDateRange,
  } = useKnowledgeGapsSearchParams();

  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [knowledgeGaps, setKnowledgeGaps] = useState<OpportunityGroupRow[]>([]);
  const [knowledgeGapsExists, setKnowledgeGapsExists] =
    useState<boolean>(false);
  const [selectedGapId, setSelectedGapId] = useState<string | null>(null);
  const [selectedGapData, setSelectedGapData] = useState<OpportunityGroupRow | null>(null);
  const isFirstRender = useRef(true);

  const { data: opportunityGroups, isLoading } = useOpportunityGroups({
    findBy: searchPhrase,
    status: status,
    startDate: dateRange.dateFrom,
    page: page,
    endDate: dateRange.dateTo,
    type: ["knowledge"],
    enabled: true,
    domain: domain,
  });

  useEffect(() => {
    setPage(1);
    setKnowledgeGaps([]);
    if (!isFirstRender.current) {
      trackFilterApplied('knowledge_gaps_filter', { searchPhrase, status });
      trackDateRangeChanged({
        from: dateRange.dateFrom || '',
        to: dateRange.dateTo || ''
      });

    } else {
      isFirstRender.current = false;
    }
  }, [searchPhrase, status, dateRange]);

  useEffect(() => {
    setHasNextPage(opportunityGroups?.length === 30);
    if (opportunityGroups && Array.isArray(opportunityGroups)) {
      if (page === 1) {
        // Replace on first page
        setKnowledgeGaps(opportunityGroups);
      } else {
        // Append on subsequent pages, but deduplicate by id
        setKnowledgeGaps((prev) => {
          const existingIds = new Set(prev.map((gap) => gap.id));
          const newGaps = opportunityGroups.filter(
            (gap) => !existingIds.has(gap.id)
          );
          return [...prev, ...newGaps];
        });
      }
    }
  }, [opportunityGroups, page]);

  const {
    data: opportunityGroupsExists,
    isLoading: isLoadingOpportunityGroupsExists,
  } = useOpportunityGroups({
    enabled: !knowledgeGapsExists,
    type: ["knowledge"],
  });

  useEffect(() => {
    if (
      opportunityGroupsExists &&
      Array.isArray(opportunityGroupsExists) &&
      opportunityGroupsExists.length > 0
    ) {
      setKnowledgeGapsExists(true);
    }
  }, [opportunityGroupsExists]);

  const isEmpty = useMemo(
    () =>
      !isLoadingOpportunityGroupsExists &&
      !opportunityGroupsExists &&
      page === 1 &&
      opportunityGroups &&
      opportunityGroups?.length === 0,
    [knowledgeGaps, page, opportunityGroupsExists]
  );

  const getStatusCell = useCallback((status: OpportunityStatus) => {
    switch (status) {
      case "backlog":
        return (
          <Flex align="center" gap="2">
            <QuestionMarkCircledIcon />
            <Text size="2" weight="regular">
              {toCamelCaseWithFirstLetterCaps(status)}
            </Text>
          </Flex>
        );
      case "hidden":
        return (
          <Flex align="center" gap="2">
            <MinusCircledIcon color="gray" />
            <Text size="2" weight="regular" color="gray">
              Ignored
            </Text>
          </Flex>
        );
      case "todo":
        return (
          <Flex align="center" gap="2">
            <CircleIcon />
            <Text size="2" weight="regular">
              To Verify
            </Text>
          </Flex>
        );
      case "verify":
        return (
          <Flex align="center" gap="2">
            <CheckCircledIcon />
            <Text size="2" weight="regular">
              Verified
            </Text>
          </Flex>
        );
      case "solved":
        return (
          <Flex align="center" gap="2">
            <CheckCircledIcon color="green" />
            <Text size="2" weight="regular" color="green">
              Solved
            </Text>
          </Flex>
        );
      default:
        return null;
    }
  }, []);

  const tableColumns = useMemo<ColumnDef<OpportunityGroupRow>[]>(() => {
    const baseColumns: ColumnDef<OpportunityGroupRow>[] = [
      {
        id: "label",
        header: "Knowledge Gaps",
        accessorKey: "label",
        cell: ({ row }) => {
          return (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                minWidth: 0,
              }}
            >
              <Text size="2" weight="regular">
                {row.original.label}
              </Text>
            </div>
          );
        },
      meta: {
        className: "w-[320px]",
        cellTooltip: true,
      },
      },
    ];

    if (selectedGapId) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        id: "allTimeSpanTickets",
        header: "Related Tickets",
        accessorKey: "allTimeSpanTickets",
        cell: ({ row }) => {
          return (
            <Text size="2">
              {row.original.allTimeSpanTickets}
            </Text>
          );
        },
      },
      {
        id: "currentTimeSpanTickets",
        header: "Selected Period",
        accessorKey: "currentTimeSpanTickets",
        cell: ({ row }) => {
          const gap = row.original;
          return (
            <Text size="2">
              {gap.currentTimeSpanTickets === 0 ? "none" : gap.currentTimeSpanTickets}
            </Text>
          );
        },
      },
      {
        id: "ticketTrend",
        header: "Ticket Trend",
        accessorKey: "currentTimeSpanTickets",
        cell: ({ row }) => {
          const gap = row.original;
          const ticketTrend = gap.currentTimeSpanTickets - gap.compareTimeSpanTickets;
          return <KnowledgeGapTicketsTrend ticketTrend={ticketTrend} />;
        },
      },
      {
        id: "priority",
        header: "Priority",
        accessorKey: "priority",
        cell: ({ row }) => {
          return <KnowledgeGapPriority priority={row.original.priority} />;
        },
      },
    ];
  }, [getStatusCell, selectedGapId]);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "allTimeSpanTickets", desc: true },
  ]);

  if (opportunityGroups?.type === "Error") {
    return (
      <PageEmptyState
        title={"Oops! Something went wrong"}
        description={
          "We're sorry, but we couldn't load the knowledge gap details right now. Please try again later."
        }
        svg={"knowledge-view"}
      ></PageEmptyState>
    );
  }

  if (isEmpty) {
    return (
      <PageEmptyState
        title={"No knowledge gaps available yet"}
        description={
          "There are no knowledge gaps to display right now. Once knowledge gaps are identified, they'll appear here for easy access and management."
        }
        svg={"knowledge-view"}
      >
        {!hasKnowledgeBaseIntegration && (
          <div>
            <Link to={"/getting-started"}>Connect knowledge sources</Link>
          </div>
        )}
      </PageEmptyState>
    );
  }

  return (
    <PageLayoutV2>
      <Flex height="100%" position="relative">
        <Flex
          flexShrink="1"
          width={selectedGapId ? "40%" : "100%"}
          minWidth="400px"
          maxWidth={selectedGapId ? "600px" : "100%"}
          direction="column"
          style={selectedGapId ? { borderRight: "1px solid var(--gray-6)" } : {}}
        >
          <PageHeader
            title="Knowledge Gaps"
            description="Discover and manage gaps in your support knowledge content"
            icon={
              <NoteRemoveIcon
                size={24}
                fill={PageColors[ENavigate.KNOWLEDGE_GAPS] || "gray"}
              />
            }
          />
          <Section size="2" p="6">
          <Flex direction={"column"} gap={"8"}>
            <Flex
              style={{
                padding: "0 4px",
                width: "100%",
              }}
              justify={"between"}
              align={"center"}
            >
              <Flex gap={"3"} align={"center"}>
                <Text weight={"medium"} size={"3"}>
                  Identified Knowledge Gaps
                </Text>
                <Text size={"2"} weight={"bold"} color="gray">
                  {knowledgeGaps && knowledgeGaps?.length}
                </Text>
                {!selectedGapId && (
                  <Search
                    setSearchPhrase={setSearchPhrase}
                    placeholder="Find"
                    debounce={500}
                    context="knowledge_gaps"
                    initialValue={searchPhrase}
                  />
                )}
              </Flex>
              {!selectedGapId && (
                <Flex gap={"1"} align={"center"}>
                  <MultiSelect
                    options={Object.entries(opportunityStatusesToLabels).map(
                      ([key, label]) => ({
                        value: key,
                        label: label,
                      })
                    )}
                    initialValue={status as string[]}
                    placeholder="Statuses"
                    onChange={(values) => {
                      setStatus(values as OpportunityStatus[]);
                    }}
                  />
                  <DateRangePicker value={dateRange} onChange={setDateRange} />
                </Flex>
              )}
            </Flex>
            {user?.owner.startsWith("guesty") && (
              <Flex>
                <div>
                  <Select
                    options={
                      ticketValues?.["domain"]?.map((x) => ({
                        label: x,
                        value: x,
                      })) || []
                    }
                    value={domain || ""}
                    onChange={(value) => {
                      setDomain(value);
                    }}
                    placeholder="Select a domain"
                  />
                </div>
              </Flex>
            )}
          </Flex>
          {knowledgeGaps.length === 0 && !isLoading ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              p="8"
              gap="2"
            >
              <Text size="3" color="gray" weight="medium">
                No knowledge gaps found
              </Text>
              <Text size="2" color="gray">
                Try adjusting your search or filter criteria
              </Text>
            </Flex>
          ) : (
            <ScrollArea style={{ maxHeight: "calc(100vh - 300px)" }}>
              <SimpleTable
                data={knowledgeGaps}
                columns={tableColumns}
                loading={isLoading && page === 1}
                onRowClicked={(row) => {
                  trackKnowledgeGapViewed(row.id, row.label);
                  if (selectedGapId === row.id) {
                    setSelectedGapId(null);
                    setSelectedGapData(null);
                  } else {
                    setSelectedGapId(row.id);
                    setSelectedGapData(row);
                  }
                }}
                defaultSorting={sorting}
                selectedRowId={selectedGapId || undefined}
                getRowId={(row) => row.id}
              />
            </ScrollArea>
          )}
          {hasNextPage && (
            <Flex justify="center" p="4">
              <Text
                size="2"
                color="gray"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (!isLoading) {
                    setPage(page + 1);
                  }
                }}
              >
                {isLoading ? "Loading..." : "Load more"}
              </Text>
            </Flex>
          )}
          </Section>
        </Flex>

        {selectedGapId && (() => {
          const selectedGap = selectedGapData || knowledgeGaps.find((gap) => gap.id === selectedGapId);
          return (
            <Flex
              flexGrow="1"
              width="100%"
              direction="column"
              style={{ position: "relative" }}
            >
              <Flex
                p="4"
                align="center"
                justify="between"
                style={{ borderBottom: "1px solid var(--gray-6)" }}
              >
                <Text size="2" weight="medium" style={{ fontWeight: 500 }}>
                  {selectedGap?.label || "Knowledge Gap Details"}
                </Text>
              <IconButton
                variant="ghost"
                size="1"
                onClick={() => {
                  setSelectedGapId(null);
                  setSelectedGapData(null);
                }}
                aria-label="Close"
              >
                  <Cross2Icon width={14} height={14} />
                </IconButton>
              </Flex>
            <ScrollArea style={{ maxHeight: "calc(100vh - 50px)" }}>
              <KnowledgeGapDetail 
                gapId={selectedGapId} 
                onClose={() => {
                  setSelectedGapId(null);
                  setSelectedGapData(null);
                }}
                dateRange={dateRange}
                allTimeSpanTickets={selectedGapData?.allTimeSpanTickets}
              />
            </ScrollArea>
          </Flex>
          );
        })()}
      </Flex>
    </PageLayoutV2>
  );
};
